import classNames from 'classnames';
import { useCallback } from 'react';

import AsyncCreatableSelect from 'react-select/async-creatable';

import { selectStyles } from '../../../constants/select';
// hooks
import { useApiRequest } from '../../../hooks/useApiRequest';
import type { FieldChangeEvent } from '../../../types';
import type { CompanyOption } from '../../../types/registration';
import InputError from '../inputError';

interface Props {
  label: string;
  name: string;
  placeholder?: string;
  hideLabel?: boolean;
  // '' until a company has been picked, then the picked option.
  value?: CompanyOption | '';
  error?: boolean;
  errorMsg?: string;
  handleChange?: (event: FieldChangeEvent<CompanyOption | null>) => void;
}

const ClientSelect = ({
  label,
  placeholder,
  hideLabel = true,
  name,
  value = '',
  error = false,
  errorMsg = '',
  handleChange = () => null,
}: Props) => {
  const apiRequest = useApiRequest();

  const getClientOptions = useCallback(
    async (inputValue: string): Promise<CompanyOption[]> => {
      if (!inputValue || inputValue.length < 2) {
        return [];
      }

      const {
        data: { clients },
      } = await apiRequest.get<{
        clients: Array<{ id: number; name: string }>;
      }>(`/v1/clients/search?query=${inputValue}`);

      return clients.map(client => ({
        value: client.id,
        label: client.name,
      }));
    },
    [apiRequest]
  );

  const getCreateLabel = (inputValue: string) => {
    return `Add new Company: ${inputValue}`;
  };

  const buildEvent = (eventValue: CompanyOption | null) => ({
    currentTarget: {
      name,
      value: eventValue,
      type: 'clientSelect',
    },
  });

  const handleChangeOption = (newValue: CompanyOption | null) => {
    const event = buildEvent(newValue);
    handleChange(event);
  };

  const handleCreateOption = (newLabel: string) => {
    const event = buildEvent({
      value: 0,
      label: newLabel,
    });
    handleChange(event);
  };

  const classes = classNames({
    field: true,
    'field--client-select': true,
    'field--hidden-label': hideLabel,
    'field--has-error': !!error,
  });

  const dropdownIndicator = () => ({ display: 'none' });
  const clientSelectStyles = { ...selectStyles, dropdownIndicator };

  return (
    <div className={classes} data-testid="clientSelect">
      <label className="field__label" htmlFor={name}>
        {label}
      </label>
      <AsyncCreatableSelect<CompanyOption, false>
        placeholder={placeholder}
        name={name}
        styles={clientSelectStyles}
        cacheOptions
        defaultOptions
        isClearable
        loadOptions={getClientOptions}
        formatCreateLabel={getCreateLabel}
        classNamePrefix={name}
        value={value === '' ? null : value}
        onChange={handleChangeOption}
        onCreateOption={handleCreateOption}
      />
      {error && <InputError errorMsg={errorMsg} />}
    </div>
  );
};

export default ClientSelect;
